import { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import i18next from 'i18next';

import { TechnicalData } from 'types/api/product.type';

import { useAppSelector } from 'redux/hooks';

import { ProductParams, productDataService } from 'services/products.service';

import { LayoutContext, SingleProductContext } from 'contexts';

import { useCatalogueLabels, useFetch } from 'hooks';

import { buildCategoryBreadcrumbSecondary } from 'helpers/buildCategoryBreadcrumb';
import { checkParams } from 'helpers/checkParams';
import { getSearchParams } from 'helpers/getSearchParams';

const useProduct = () => {
  const { breadcrumbSet, breadcrumbSecondarySet, additionalInfoSet } =
    useContext(LayoutContext);
  const { product, productSet, isProductModalVisibleSet, image, title } =
    useContext(SingleProductContext);

  const [searchParams] = useSearchParams();

  const { genre, category, id } = useParams();
  const { genreLabel, categoryLabel } = useCatalogueLabels();

  const { language } = i18next;

  const [technicalData, technicalDataSet] = useState<TechnicalData>();

  const { season, marketLine, warehouse, zone } = useAppSelector((state) => state.settings);

  const detailsApiParams: ProductParams = {
    season,
    marketLine,
    warehouse: warehouse.code,
    zone,
    genre,
    category,
    ...getSearchParams(searchParams),
  };

  const productDetailsFetch = useFetch(
    checkParams(detailsApiParams)
      ? () => productDataService.getProductDetails(detailsApiParams)
      : undefined,
    [season, marketLine, genre, category, zone, warehouse, id]
  );

  const technicalApiParams: ProductParams = {
    ...detailsApiParams,
    withSize: 'false',
  };

  const technicalDataFetch = useFetch(
    checkParams(technicalApiParams)
      ? () => productDataService.getTechnicalData(technicalApiParams)
      : undefined,
    [season, marketLine, genre, category, zone, warehouse, id]
  );

  useEffect(() => {
    if (product) {
      const breadcrumb = [
        {
          label: title,
        },
      ];

      const additionalInfo = [
        language === 'en' ? product.englishItemMainTitle : product.italianItemMainTitle,
        `${product.mainFabricCode} ${product.mainFabricDesc}`,
        `${product?.colorUsedInSample ? '*' : ''} ${product.colorCode} ${
          language === 'en' ? product.englishColorDescription : product.italianColorDescription
        }`,
      ];

      breadcrumbSet(breadcrumb);
      additionalInfoSet(additionalInfo);
    }
  }, [product, title]);

  useEffect(() => {
    if (genre && season && marketLine && category) {
      breadcrumbSecondarySet(
        buildCategoryBreadcrumbSecondary(
          season,
          marketLine,
          genre,
          category,
          genreLabel(genre),
          categoryLabel(category)
        )
      );
    }
  }, [season, marketLine, genre, category, title, genreLabel, categoryLabel]);

  useEffect(() => {
    if (technicalDataFetch.content) {
      technicalDataSet(technicalDataFetch.content);
    }
  }, [technicalDataFetch]);

  useEffect(() => {
    if (productDetailsFetch.content) {
      productSet(productDetailsFetch.content);
    }
  }, [productDetailsFetch.content]);

  return {
    product,
    isProductModalVisibleSet,
    technicalDataFetch,
    image,
    technicalData,
    language,
    productDetailsFetch,
    title,
  };
};

export { useProduct };
